import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Placeholder from "../../components/images/placeholder.png";
import { fetchPublicProject } from "../../api/projects";
import { ResourceLoading } from "../../components/utils/ResourceLoading";
import { ResourceError } from "../../components/utils/ResourceError";
import { ContactCTA } from "../../components/home/ContactCTA";
import { FadeIn, FadeInStagger } from "../../components/utils/FadeIn";

/**
 * Project detail — Airon design.
 *
 * Viser Promang-prosjekt med:
 *  - Eyebrow / tittel / beskrivelse (Airon dark hero)
 *  - Hovedbilde i stor format
 *  - Metadata (kunde, dato, kontraktsum, status)
 *  - Content lines fra Promang (website_content_lines)
 *  - Tilbake-lenke til referanselista
 */
export const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!id) {
        setError("Prosjekt ID mangler");
        setIsLoading(false);
        return;
      }

      try {
        const res = await fetchPublicProject(id);
        if (cancelled) return;
        if (res?.id) {
          setProject(res);
        } else {
          setError(res?.error || "Kunne ikke hente prosjekt");
        }
      } catch (e) {
        console.error("Error loading project:", e);
        if (!cancelled) setError("Kunne ikke hente prosjekt nå.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const getDisplayTitle = (p) => p?.public_title || (p?.id ? `Prosjekt ${p.id}` : "");
  const getDisplayDescription = (p) => p?.public_description || "";

  const getImageUrl = (p) => {
    if (!p) return null;
    if (p.public_main_image?.file?.url) return p.public_main_image.file.url;
    if (p.public_main_image?.file_url) return p.public_main_image.file_url;
    if (p.public_main_image?.url) return p.public_main_image.url;
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("no-NO", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatYear = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";
    return String(date.getFullYear());
  };

  const getDateRange = (p) => {
    const startYear = formatYear(p?.start_date);
    const endYear = formatYear(p?.end_date);
    if (startYear && endYear && startYear !== endYear) {
      return `${startYear} – ${endYear}`;
    }
    return startYear || endYear || "";
  };

  /**
   * Ekte ferdig-sjekk — match logikken i listevisningen. Returnerer null hvis
   * prosjektet ikke kan dokumenteres som ferdig; da vises ingen statustag.
   */
  const isCompleted = (p) => {
    if (!p) return false;
    const statusStr = String(
      p.status || p.state || p.progress_status || "",
    ).toLowerCase();
    if (
      ["completed", "finished", "done", "ferdig", "levert", "closed", "delivered"].includes(
        statusStr,
      )
    ) {
      return true;
    }
    if (p.completed === true || p.is_completed === true) return true;
    if (p.finished_at || p.delivered_at || p.completed_at) return true;
    if (p.end_date) {
      const end = new Date(p.end_date);
      if (!Number.isNaN(end.getTime()) && end.getTime() <= Date.now()) {
        return true;
      }
    }
    return false;
  };

  const getStatus = (p) => {
    if (!isCompleted(p)) return null;
    return p?.end_date ? { label: "Levert" } : { label: "Ferdig" };
  };

  /**
   * Formaterer kontraktsum med norske tusen-skiller og " kr"-suffiks.
   * Tåler både tall og allerede formaterte strenger.
   */
  const formatContractSum = (value) => {
    if (value === null || value === undefined || value === "") return "";

    // Allerede formatert tekst — returner som den er.
    if (typeof value === "string" && /[a-zA-Z]/.test(value)) return value;

    const numValue =
      typeof value === "string"
        ? parseFloat(value.replace(/\s/g, "").replace(",", "."))
        : Number(value);

    if (!Number.isFinite(numValue) || numValue === 0) return "";

    // Hele kroner (runder ned desimalstøy under 1 kr) – beholder øre hvis satt.
    const hasDecimals = Math.abs(numValue % 1) > 0.00001;
    const fixed = hasDecimals ? numValue.toFixed(2) : numValue.toFixed(0);
    const [intPart, decPart] = fixed.split(".");
    const withSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    const body = decPart ? `${withSep},${decPart}` : withSep;
    return `${body} kr`;
  };

  // --------- Render states -----------
  if (isLoading) {
    return (
      <section className="sec" style={{ paddingTop: 120 }}>
        <div className="wrap">
          <ResourceLoading />
        </div>
      </section>
    );
  }

  if (error || !project) {
    return (
      <section className="sec" style={{ paddingTop: 120 }}>
        <div className="wrap">
          <ResourceError errors={[error || "Prosjekt ikke funnet"]} />
          <div style={{ marginTop: 24 }}>
            <Link to="/om-oss/prosjekter" className="btn ghost">
              <span className="arr" style={{ marginRight: 6 }}>
                ←
              </span>{" "}
              Tilbake til prosjekter
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const imageUrl = getImageUrl(project);
  const title = getDisplayTitle(project);
  const description = getDisplayDescription(project);
  const dateRange = getDateRange(project);
  const status = getStatus(project);
  const contractSum = formatContractSum(project.public_contract_sum);
  const client = project.public_client || "";
  const startDateFmt = formatDate(project.start_date);
  const endDateFmt = formatDate(project.end_date);
  const hasContent =
    Array.isArray(project.website_content_lines) &&
    project.website_content_lines.length > 0;

  /** Promang may send line_type, lineType, or (after normalization) "statistics" | "info". */
  const lineTypeOf = (l) =>
    (l && (l.line_type ?? l.lineType ?? l["line_type"] ?? l["lineType"])) || "";
  const isStatistics = (l) => {
    const t = String(lineTypeOf(l)).toLowerCase().trim();
    return t === "statistics" || t === "statistikk" || t === "stats";
  };

  const stats = (project.website_content_lines || []).filter((l) => isStatistics(l));
  const info = (project.website_content_lines || []).filter((l) => !isStatistics(l));

  // --------- Hovedvisning -----------
  return (
    <>
      {/* HERO — Airon dark eyebrow + display title + status-chip */}
      <FadeInStagger className="contact-cta" style={{ padding: "80px 0 56px" }}>
        <div className="wrap">
          <div style={{ maxWidth: "58rem", position: "relative" }}>
            <FadeIn>
              <div
                className="eyebrow"
                style={{
                  background: "rgba(57,182,234,0.14)",
                  color: "#B5E5FA",
                  marginBottom: 22,
                }}
              >
                <span className="dot" />
                Prosjekt · Referanse
              </div>
            </FadeIn>

            <FadeIn>
              <h1
                className="display"
                style={{
                  fontFamily: "var(--display)",
                  fontWeight: 500,
                  fontSize: "clamp(32px, 4.6vw, 52px)",
                  lineHeight: 1.05,
                  letterSpacing: "-0.025em",
                  margin: 0,
                  color: "#fff",
                }}
              >
                {title}
              </h1>
            </FadeIn>

            <FadeIn>
              <div
                style={{
                  marginTop: 22,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 10,
                  alignItems: "center",
                }}
              >
                {status ? (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 12px",
                      borderRadius: 999,
                      border: "1px solid rgba(181,229,250,0.35)",
                      background: "rgba(57,182,234,0.12)",
                      color: "#B5E5FA",
                      fontFamily: "var(--mono)",
                      fontSize: 11,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                    }}
                  >
                    {status.label}
                  </span>
                ) : null}
                {dateRange ? (
                  <span
                    style={{
                      padding: "6px 12px",
                      borderRadius: 999,
                      border: "1px solid rgba(255,255,255,0.12)",
                      color: "#B5C4D3",
                      fontFamily: "var(--mono)",
                      fontSize: 11,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                    }}
                  >
                    {dateRange}
                  </span>
                ) : null}
                {client ? (
                  <span
                    style={{
                      padding: "6px 12px",
                      borderRadius: 999,
                      border: "1px solid rgba(255,255,255,0.12)",
                      color: "#B5C4D3",
                      fontFamily: "var(--mono)",
                      fontSize: 11,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                    }}
                  >
                    {client}
                  </span>
                ) : null}
              </div>
            </FadeIn>

            <FadeIn>
              <div style={{ marginTop: 28 }}>
                <Link
                  to="/om-oss/prosjekter"
                  className="btn ghost"
                  style={{
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.22)",
                  }}
                >
                  <span style={{ marginRight: 6 }}>←</span>
                  Alle prosjekter
                </Link>
              </div>
            </FadeIn>
          </div>
        </div>
      </FadeInStagger>

      {/* HERO IMAGE */}
      {imageUrl ? (
        <section style={{ background: "var(--bg-2)", padding: "0" }}>
          <div className="wrap" style={{ padding: "40px 20px" }}>
            <FadeIn>
              <div
                style={{
                  position: "relative",
                  borderRadius: 20,
                  overflow: "hidden",
                  border: "1px solid var(--line)",
                  background: "#fff",
                  aspectRatio: "16 / 9",
                  marginTop: "-64px",
                  boxShadow: "0 30px 80px rgba(14,34,51,0.12)",
                }}
              >
                <img
                  src={imageUrl}
                  alt={title}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = Placeholder;
                  }}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </div>
            </FadeIn>
          </div>
        </section>
      ) : null}

      {/* INNHOLD */}
      <section className="sec" style={{ borderTop: "none" }}>
        <div className="wrap">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) 320px",
              gap: 48,
              alignItems: "start",
            }}
            className="project-detail-grid"
          >
            {/* Venstre: beskrivelse + content lines */}
            <div>
              {description ? (
                <FadeIn>
                  <div className="sec-tag" style={{ marginBottom: 12 }}>
                    Om prosjektet
                  </div>
                  <p
                    style={{
                      fontSize: 18,
                      lineHeight: 1.65,
                      color: "var(--ink)",
                      whiteSpace: "pre-wrap",
                      margin: 0,
                    }}
                  >
                    {description}
                  </p>
                </FadeIn>
              ) : null}

              {/* Statistics lines — tall fra Promang */}
              {stats.length > 0 ? (
                <FadeIn>
                  <div style={{ marginTop: description ? 40 : 0 }}>
                    <div className="sec-tag" style={{ marginBottom: 16 }}>
                      Nøkkeltall
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${Math.min(
                          stats.length,
                          3,
                        )}, minmax(0, 1fr))`,
                        gap: 16,
                      }}
                    >
                      {stats.map((line, i) => (
                        <div
                          key={`stat-${i}`}
                          style={{
                            background: "var(--bg-2)",
                            border: "1px solid var(--line)",
                            borderRadius: 16,
                            padding: "22px 22px 20px",
                          }}
                        >
                          <div
                            style={{
                              fontFamily: "var(--display)",
                              fontWeight: 500,
                              fontSize: "clamp(24px, 3vw, 34px)",
                              letterSpacing: "-0.02em",
                              color: "var(--ink)",
                              lineHeight: 1.1,
                            }}
                          >
                            {line.value}
                          </div>
                          <div
                            style={{
                              marginTop: 8,
                              fontFamily: "var(--mono)",
                              fontSize: 11,
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              color: "var(--muted)",
                            }}
                          >
                            {line.title}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </FadeIn>
              ) : null}

              {/* Info / innholdslinjer */}
              {info.length > 0 ? (
                <FadeIn>
                  <div style={{ marginTop: 40 }}>
                    <div className="sec-tag" style={{ marginBottom: 16 }}>
                      Detaljer
                    </div>
                    <dl
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gap: 14,
                        margin: 0,
                      }}
                    >
                      {info.map((line, i) => (
                        <div
                          key={`info-${i}`}
                          style={{
                            background: "#fff",
                            border: "1px solid var(--line)",
                            borderRadius: 14,
                            padding: "16px 18px",
                          }}
                        >
                          <dt
                            style={{
                              fontFamily: "var(--mono)",
                              fontSize: 11,
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              color: "var(--muted)",
                              margin: 0,
                            }}
                          >
                            {line.title}
                          </dt>
                          <dd
                            style={{
                              margin: "6px 0 0",
                              color: "var(--ink)",
                              fontSize: 15,
                              lineHeight: 1.5,
                            }}
                          >
                            {line.value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </FadeIn>
              ) : null}
            </div>

            {/* Høyre sidebar: fakta */}
            <FadeIn>
              <aside
                style={{
                  background: "#fff",
                  border: "1px solid var(--line)",
                  borderRadius: 18,
                  padding: "22px 22px 18px",
                  position: "sticky",
                  top: 96,
                }}
              >
                <div
                  className="sec-tag"
                  style={{ marginBottom: 14, color: "var(--airon-3)" }}
                >
                  Fakta
                </div>

                <FactRow
                  label="Status"
                  value={status ? status.label : "Pågående"}
                />
                {client ? <FactRow label="Kunde" value={client} /> : null}
                {startDateFmt ? (
                  <FactRow label="Oppstart" value={startDateFmt} />
                ) : null}
                {endDateFmt ? (
                  <FactRow label="Levert" value={endDateFmt} />
                ) : null}
                {contractSum ? (
                  <FactRow label="Kontraktsum" value={contractSum} />
                ) : null}

                <div style={{ marginTop: 18 }}>
                  <Link to="/kontakt" className="btn" style={{ width: "100%" }}>
                    Be om lignende <span className="arr">→</span>
                  </Link>
                </div>
              </aside>
            </FadeIn>
          </div>
        </div>

        {/* Liten responsiv override for mobile — gjør grid 1 kolonne */}
        <style>{`
          @media (max-width: 900px) {
            .project-detail-grid { grid-template-columns: 1fr !important; }
            .project-detail-grid aside { position: static !important; }
          }
        `}</style>
      </section>

      {/* Tom-state hvis verken content-lines eller beskrivelse */}
      {!description && !hasContent ? (
        <section className="sec" style={{ borderTop: "none", paddingTop: 0 }}>
          <div className="wrap">
            <div
              style={{
                padding: 28,
                background: "var(--bg-2)",
                border: "1px dashed var(--line)",
                borderRadius: 14,
                color: "var(--muted)",
                fontFamily: "var(--mono)",
                fontSize: 12,
                letterSpacing: "0.04em",
              }}
            >
              Prosjektet er registrert i Promang, men har foreløpig ikke mer
              detaljert innhold.
            </div>
          </div>
        </section>
      ) : null}

      <ContactCTA
        title="Har du et lignende prosjekt"
        highlight="som skal løses?"
        description="Vi tar en uforpliktende prat, vurderer behovet og gir deg et konkret tilbud."
      />
    </>
  );
};

/** Liten hjelpe-komponent for faktarader i sidebar. */
function FactRow({ label, value }) {
  return (
    <div
      style={{
        padding: "10px 0",
        borderTop: "1px solid var(--line)",
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <span
        style={{
          fontFamily: "var(--mono)",
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "var(--muted)",
          flex: "0 0 auto",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 14,
          color: "var(--ink)",
          textAlign: "right",
          fontWeight: 500,
        }}
      >
        {value}
      </span>
    </div>
  );
}
